import React, { useMemo, useState } from "react";
import {
  Plus,
  Search,
  Pencil,
} from "lucide-react";

import RightDrawer from "../../shared/components/RightDrawer";
import StatusBadge from "../../shared/components/StatusBadge";

const DEFAULT_ENUM_TYPES = [
  {
    uuid: "type-ft",
    type_name: "Full Time",
    type_code: "FT",
    description: "Permanent full-time employment for employees working standard working hours.",
    status: "Active",
  },
  {
    uuid: "type-pt",
    type_name: "Part Time",
    type_code: "PT",
    description: "Part-time employment for employees working reduced working hours.",
    status: "Active",
  },
  {
    uuid: "type-ct",
    type_name: "Contract",
    type_code: "CT",
    description: "Contract-based employment for a fixed period or specific business requirement.",
    status: "Active",
  },
  {
    uuid: "type-temp",
    type_name: "Temporary",
    type_code: "TEMP",
    description: "Temporary employment for short-term business requirements.",
    status: "Active",
  },
  {
    uuid: "type-intern",
    type_name: "Internship",
    type_code: "INTERN",
    description: "Employment type for students or trainees working for a limited period.",
    status: "Active",
  },
  {
    uuid: "type-consult",
    type_name: "Consultant",
    type_code: "CONSULT",
    description: "Independent consultant engaged for specialized professional services.",
    status: "Active",
  },
  {
    uuid: "type-freelance",
    type_name: "Freelancer",
    type_code: "FREELANCE",
    description: "Freelance employment for project-based or task-based assignments.",
    status: "Active",
  },
  {
    uuid: "type-probation",
    type_name: "Probationary",
    type_code: "PROBATION",
    description: "Employment type for employees currently serving their probation period.",
    status: "Active",
  },
  {
    uuid: "type-remote",
    type_name: "Remote Full Time",
    type_code: "REMOTE",
    description: "Full-time employment where the employee works remotely.",
    status: "Active",
  },
];

const EmploymentTypeDrawer = ({
  isOpen = false,
  open = false,
  onClose,
  onAdd,
  onEdit,
  employmentTypes = [],
  employees = [],
}) => {
  const drawerOpen = isOpen || open;
  const rawTypesList = (employmentTypes && employmentTypes.length > 0) ? employmentTypes : DEFAULT_ENUM_TYPES;

  // Deduplicate employment types strictly by type_code, type_name, or uuid
  const activeTypesList = useMemo(() => {
    const seen = new Set();
    return rawTypesList.filter((type) => {
      const codeKey = (type.type_code || "").toLowerCase().trim();
      const nameKey = (type.type_name || "").toLowerCase().trim();
      const key = codeKey || nameKey || (type.uuid || "").toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rawTypesList]);

  const [search, setSearch] = useState("");

  // Dynamically calculate employee count from employee table rows
  const computedTypes = useMemo(() => {
    return activeTypesList.map((type) => {
      const count = employees.filter((emp) => {
        const empText = `${emp.employment_type_name || ""} ${emp.contract_type || ""} ${emp.remarks || ""} ${emp.designation || ""}`.toLowerCase();
        const nameKey = (type.type_name || "").toLowerCase().trim();
        const codeKey = (type.type_code || "").toLowerCase().trim();

        if (nameKey && empText.includes(nameKey)) return true;
        if (codeKey && (empText.includes(codeKey) || codeKey === "ft" && empText.includes("full"))) return true;
        if (nameKey.includes("full") && (empText.includes("full") || empText.includes("permanent"))) return true;
        if (nameKey.includes("part") && empText.includes("part")) return true;
        if (nameKey.includes("intern") && empText.includes("intern")) return true;
        if (nameKey.includes("contract") && empText.includes("contract")) return true;
        return false;
      }).length;

      return {
        ...type,
        employee_count: count > 0 ? count : (type.employee_count || 0),
      };
    });
  }, [activeTypesList, employees]);

  const filteredTypes = useMemo(() => {
    const query = search
      .trim()
      .toLowerCase();

    if (!query) {
      return computedTypes;
    }

    return computedTypes.filter(
      (type) => {
        const searchableText = [
          type.type_name,
          type.type_code,
          type.description,
          type.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(
          query
        );
      }
    );
  }, [employmentTypes, search]);

  if (!drawerOpen) {
    return null;
  }

  return (
    <RightDrawer
      open={drawerOpen}
      onClose={onClose}
      title="Employment Type"
      description="Manage employment classifications and their default settings."
      width="620px"
    >
      <div className="flex h-full flex-col">
        {/* Toolbar */}
        <div className="shrink-0 border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative min-w-0 flex-1">
              <Search
                size={16}
                strokeWidth={1.8}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="text"
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Search employment types..."
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none placeholder:text-slate-400 focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10"
              />
            </div>

            {/* Add */}
            <button
              type="button"
              onClick={onAdd}
              className="flex h-10 shrink-0 items-center gap-2 rounded-lg bg-[#147db5] px-3.5 text-sm font-medium text-white transition hover:bg-[#116d9d]"
            >
              <Plus size={16} strokeWidth={2} />
              <span>Add</span>
            </button>
          </div>
        </div>

        {/* Employment Type List */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {filteredTypes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70">
                    <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Employment Type
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Code
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                    </th>

                    <th className="w-12 px-3 py-3" />
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {filteredTypes.map(
                    (type) => (
                      <tr
                        key={type.uuid}
                        className="group transition-colors hover:bg-slate-50"
                      >
                        {/* Type */}

                        <td className="px-5 py-4">
                          <div>
                            <p className="text-sm font-medium text-slate-800">
                              {type.type_name ||
                                "Unnamed Type"}
                            </p>

                            {type.description && (
                              <p className="mt-0.5 max-w-[240px] truncate text-xs text-slate-400">
                                {type.description}
                              </p>
                            )}
                          </div>
                        </td>

                        {/* Code */}

                        <td className="px-4 py-4">
                          <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
                            {type.type_code ||
                              "—"}
                          </span>
                        </td>

                        {/* Status */}

                        <td className="px-4 py-4">
                          <StatusBadge
                            status={
                              type.status
                            }
                          />
                        </td>

                        {/* Edit */}

                        <td className="px-3 py-4">
                          <button
                            type="button"
                            onClick={() =>
                              onEdit?.(
                                type
                              )
                            }
                            className="
                              flex
                              h-8
                              w-8
                              items-center
                              justify-center
                              rounded-lg
                              text-slate-400
                              opacity-0
                              transition
                              hover:bg-slate-100
                              hover:text-slate-700
                              group-hover:opacity-100
                            "
                            aria-label={`Edit ${
                              type.type_name ||
                              "employment type"
                            }`}
                          >
                            <Pencil
                              size={15}
                              strokeWidth={1.8}
                            />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex h-64 items-center justify-center px-6">
              <div className="text-center">
                <p className="text-sm font-medium text-slate-700">
                  No employment types found
                </p>

                <p className="mt-1 text-xs text-slate-400">
                  Try a different search term.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER */}

        <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-3">
          <p className="text-xs text-slate-400">
            {filteredTypes.length} employment{" "}
            {filteredTypes.length === 1
              ? "type"
              : "types"}
          </p>
        </div>
      </div>
    </RightDrawer>
  );
};

export default EmploymentTypeDrawer;